import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ComboboxField } from 'components/DesignSystems/ComboboxField/ComboboxField';
import type { CustomEventActionInput } from 'hooks/customEvents/useCustomEvents';
import { useRosters } from 'hooks/useRosters';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { removeTaskConfirmation } from './eventConfirmations';
import { toOptions, type EventFormValues } from './eventForm';

const ACTION_SEVERITIES: NonNullable<CustomEventActionInput['severity']>[] = [
  'low',
  'normal',
  'high',
  'critical',
];

const ResponsePlanSection: React.FC = () => {
  const {
    control,
    register,
    getValues,
    formState: { errors },
  } = useFormContext<EventFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'actions',
  });
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null
  );
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster = [] } = useRosters(groupGuid ? [groupGuid] : []);
  const assigneeItems = useMemo(
    () =>
      roster.map((member) => ({
        value: member.user_guid,
        label: member.user_name || member.email_address,
      })),
    [roster]
  );

  const pendingRemoveName =
    pendingRemoveIndex === null
      ? ''
      : getValues(`actions.${pendingRemoveIndex}.name`).trim() ||
        `Task ${pendingRemoveIndex + 1}`;

  const removeText = removeTaskConfirmation(pendingRemoveName);

  const confirmRemove = () => {
    if (pendingRemoveIndex !== null) {
      remove(pendingRemoveIndex);
    }
    setPendingRemoveIndex(null);
  };

  return (
    <div className="tw:grid tw:gap-3">
      {fields.length === 0 && <p className="mb-0">No tasks added.</p>}
      {fields.map((field, index) => {
        const nameError = errors.actions?.[index]?.name;
        return (
          <div
            key={field.id}
            className="tw:grid tw:gap-4 tw:rounded-md tw:border tw:border-border tw:p-3"
          >
            <div className="tw:flex tw:items-center tw:justify-between">
              <span className="fw-bold">Task {index + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove task ${index + 1}`}
                onClick={() => setPendingRemoveIndex(index)}
              >
                <Trash2 />
              </Button>
            </div>
            <Field data-invalid={!!nameError}>
              <FieldLabel htmlFor={`new-event-action-${index}-name`}>
                Task *
              </FieldLabel>
              <Input
                id={`new-event-action-${index}-name`}
                aria-invalid={!!nameError}
                {...register(`actions.${index}.name`, {
                  validate: (value) =>
                    value.trim().length > 0 || 'Task name is required',
                  maxLength: { value: 255, message: 'Task name is too long' },
                })}
              />
              <FieldError errors={[nameError]} />
            </Field>
            <Field>
              <FieldLabel htmlFor={`new-event-action-${index}-description`}>
                Description
              </FieldLabel>
              <Textarea
                id={`new-event-action-${index}-description`}
                rows={3}
                {...register(`actions.${index}.description`)}
              />
            </Field>
            <div className="tw:grid tw:grid-cols-2 tw:gap-4">
              <Controller
                control={control}
                name={`actions.${index}.poc`}
                render={({ field }) => (
                  <ComboboxField
                    label="Assignee"
                    options={assigneeItems}
                    placeholder="Unassigned"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name={`actions.${index}.severity`}
                render={({ field }) => (
                  <ComboboxField
                    label="Severity"
                    options={toOptions(ACTION_SEVERITIES)}
                    placeholder="None"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        className="tw:justify-self-start"
        onClick={() =>
          append({ name: '', description: '', severity: null, poc: null })
        }
      >
        <Plus data-icon="inline-start" />
        Add Task
      </Button>
      <AlertDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(open) => !open && setPendingRemoveIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{removeText.dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {removeText.dialogContent}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{removeText.dialogCloseText}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmRemove}>
              {removeText.dialogButtonText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ResponsePlanSection;
